import React, { useState, Component } from 'react';
import { ChevronDown, ChevronRight, Info, Star, BookOpen, Users, Briefcase, Zap, BarChart2, X, UserIcon, TrendingUp, Award, BarChart, FileText, Clipboard, CheckCircle, Calendar, PieChart } from 'lucide-react';
// Skill types and data structure
type SkillLevel = 'beginner' | 'intermediate' | 'advanced' | 'expert' | 'master';
type AssessmentFactor = 'mentoring' | 'training' | 'projects' | 'outcomes' | 'peer_review';
type AssessmentEntry = {
  id: string;
  date: string;
  factor: AssessmentFactor;
  description: string;
  impact: number; // 0-100 scale of impact
  weight: number; // 0-1 scale of weight in overall assessment
};
type Skill = {
  id: string;
  name: string;
  category: string;
  level: SkillLevel;
  description: string;
  lastAssessed?: string;
  growth?: number; // Percentage growth in the last period
  assessmentHistory?: AssessmentEntry[];
};
// Mock company comparison data
type CompanyComparisonData = {
  skillId: string;
  averageLevel: number; // 0-100 scale
  distribution: Record<SkillLevel, number>; // percentage in each level
  userPercentile: number; // 0-100, where the user ranks
  totalUsers: number;
  departmentAverage?: number; // optional department average
};
// Convert skill level to numeric value for positioning
const levelToValue = {
  beginner: 0,
  intermediate: 25,
  advanced: 50,
  expert: 75,
  master: 100
};
// Convert numeric value to skill level
const valueToLevel = (value: number): SkillLevel => {
  if (value < 12.5) return 'beginner';
  if (value < 37.5) return 'intermediate';
  if (value < 62.5) return 'advanced';
  if (value < 87.5) return 'expert';
  return 'master';
};
// Convert skill level to color
const levelToColor = {
  beginner: 'bg-blue-200',
  intermediate: 'bg-blue-300',
  advanced: 'bg-blue-400',
  expert: 'bg-blue-500',
  master: 'bg-blue-600'
};
// Factor to color mapping
const factorToColor = {
  mentoring: 'bg-purple-500',
  training: 'bg-blue-500',
  projects: 'bg-green-500',
  outcomes: 'bg-yellow-500',
  peer_review: 'bg-pink-500'
};
// Factor to display name mapping
const factorToName = {
  mentoring: 'Mentoring Activities',
  training: 'Training & Classes',
  projects: 'Project Experience',
  outcomes: 'Business Outcomes',
  peer_review: 'Peer Review'
};
// Factor to icon mapping
const factorToIcon = (factor: AssessmentFactor) => {
  switch (factor) {
    case 'mentoring':
      return <Users className="w-4 h-4" data-id="element-955" />;
    case 'training':
      return <BookOpen className="w-4 h-4" data-id="element-956" />;
    case 'projects':
      return <Clipboard className="w-4 h-4" data-id="element-957" />;
    case 'outcomes':
      return <CheckCircle className="w-4 h-4" data-id="element-958" />;
    case 'peer_review':
      return <Star className="w-4 h-4" data-id="element-959" />;
    default:
      return null;
  }
};
// Sample skill data
const skillsData: Skill[] = [
// Technical Skills
{
  id: 'skill-1',
  name: 'JavaScript',
  category: 'Technical',
  level: 'advanced',
  description: 'Modern JavaScript including ES6+ features',
  lastAssessed: '2023-12-01',
  growth: 15,
  assessmentHistory: [{
    id: 'js-assess-1',
    date: '2023-12-01',
    factor: 'projects',
    description: 'Led frontend architecture for customer portal redesign',
    impact: 85,
    weight: 0.4
  }, {
    id: 'js-assess-2',
    date: '2023-11-15',
    factor: 'training',
    description: 'Advanced JavaScript Patterns workshop',
    impact: 70,
    weight: 0.2
  }, {
    id: 'js-assess-3',
    date: '2023-10-20',
    factor: 'mentoring',
    description: 'Mentored 3 junior developers on JavaScript best practices',
    impact: 65,
    weight: 0.15
  }, {
    id: 'js-assess-4',
    date: '2023-09-10',
    factor: 'outcomes',
    description: 'Reduced bundle size by 35% through code optimization',
    impact: 90,
    weight: 0.15
  }, {
    id: 'js-assess-5',
    date: '2023-08-05',
    factor: 'peer_review',
    description: 'Quarterly peer skill assessment',
    impact: 75,
    weight: 0.1
  }]
}, {
  id: 'skill-2',
  name: 'React',
  category: 'Technical',
  level: 'expert',
  description: 'Building complex applications with React and related libraries',
  lastAssessed: '2023-11-15',
  growth: 10,
  assessmentHistory: [{
    id: 'react-assess-1',
    date: '2023-11-15',
    factor: 'projects',
    description: 'Architected component library used across 5 products',
    impact: 95,
    weight: 0.35
  }, {
    id: 'react-assess-2',
    date: '2023-10-10',
    factor: 'mentoring',
    description: 'Led weekly React architecture review sessions',
    impact: 80,
    weight: 0.25
  }, {
    id: 'react-assess-3',
    date: '2023-09-15',
    factor: 'outcomes',
    description: 'Implemented performance optimizations reducing render times by 40%',
    impact: 90,
    weight: 0.2
  }, {
    id: 'react-assess-4',
    date: '2023-08-20',
    factor: 'training',
    description: 'Advanced React Patterns certification',
    impact: 85,
    weight: 0.1
  }, {
    id: 'react-assess-5',
    date: '2023-07-05',
    factor: 'peer_review',
    description: 'Department technical review',
    impact: 90,
    weight: 0.1
  }]
}, {
  id: 'skill-3',
  name: 'TypeScript',
  category: 'Technical',
  level: 'intermediate',
  description: 'Type-safe JavaScript development',
  lastAssessed: '2023-10-20',
  growth: 25,
  assessmentHistory: [{
    id: 'ts-assess-1',
    date: '2023-10-20',
    factor: 'training',
    description: 'TypeScript fundamentals and advanced types course',
    impact: 75,
    weight: 0.3
  }, {
    id: 'ts-assess-2',
    date: '2023-09-15',
    factor: 'projects',
    description: 'Migrated legacy JavaScript codebase to TypeScript',
    impact: 65,
    weight: 0.4
  }, {
    id: 'ts-assess-3',
    date: '2023-08-10',
    factor: 'outcomes',
    description: 'Reduced type-related bugs by 60% in core modules',
    impact: 70,
    weight: 0.2
  }, {
    id: 'ts-assess-4',
    date: '2023-07-05',
    factor: 'peer_review',
    description: 'Code quality assessment',
    impact: 60,
    weight: 0.1
  }]
}, {
  id: 'skill-4',
  name: 'CSS/Tailwind',
  category: 'Technical',
  level: 'advanced',
  description: 'Modern CSS techniques and Tailwind framework',
  lastAssessed: '2023-12-05',
  growth: 5
},
// Leadership Skills
{
  id: 'skill-5',
  name: 'Team Management',
  category: 'Leadership',
  level: 'intermediate',
  description: 'Managing team dynamics and performance',
  lastAssessed: '2023-11-10',
  growth: 20
}, {
  id: 'skill-6',
  name: 'Strategic Planning',
  category: 'Leadership',
  level: 'beginner',
  description: 'Developing and executing strategic initiatives',
  lastAssessed: '2023-10-15',
  growth: 30
}, {
  id: 'skill-7',
  name: 'Mentoring',
  category: 'Leadership',
  level: 'advanced',
  description: 'Guiding and developing team members',
  lastAssessed: '2023-11-25',
  growth: 15
},
// Communication Skills
{
  id: 'skill-8',
  name: 'Technical Writing',
  category: 'Communication',
  level: 'expert',
  description: 'Creating clear technical documentation',
  lastAssessed: '2023-12-10',
  growth: 5
}, {
  id: 'skill-9',
  name: 'Public Speaking',
  category: 'Communication',
  level: 'intermediate',
  description: 'Presenting to groups and at conferences',
  lastAssessed: '2023-10-30',
  growth: 10
}, {
  id: 'skill-10',
  name: 'Cross-team Collaboration',
  category: 'Communication',
  level: 'advanced',
  description: 'Working effectively across different teams and departments',
  lastAssessed: '2023-11-20',
  growth: 15
},
// Domain Knowledge
{
  id: 'skill-11',
  name: 'Industry Trends',
  category: 'Domain Knowledge',
  level: 'intermediate',
  description: 'Understanding current and emerging industry patterns',
  lastAssessed: '2023-11-05',
  growth: 20
}, {
  id: 'skill-12',
  name: 'Product Development',
  category: 'Domain Knowledge',
  level: 'advanced',
  description: 'End-to-end product development lifecycle',
  lastAssessed: '2023-12-15',
  growth: 10
}];
// Mock company comparison data
const generateCompanyComparisonData = (skillId: string): CompanyComparisonData => {
  const skill = skillsData.find(s => s.id === skillId)!;
  const userLevel = levelToValue[skill.level];
  // Generate realistic but random distribution data
  const distribution: Record<SkillLevel, number> = {
    beginner: Math.floor(Math.random() * 30) + 5,
    intermediate: Math.floor(Math.random() * 30) + 15,
    advanced: Math.floor(Math.random() * 30) + 10,
    expert: Math.floor(Math.random() * 20) + 5,
    master: Math.floor(Math.random() * 10) + 1
  };
  // Normalize to 100%
  const total = Object.values(distribution).reduce((a, b) => a + b, 0);
  Object.keys(distribution).forEach(key => {
    distribution[key as SkillLevel] = Math.round(distribution[key as SkillLevel] / total * 100);
  });
  // Calculate user percentile based on their level
  let percentileBelow = 0;
  const levels: SkillLevel[] = ['beginner', 'intermediate', 'advanced', 'expert', 'master'];
  const userLevelIndex = levels.indexOf(skill.level);
  // Add up percentages of levels below user
  for (let i = 0; i < userLevelIndex; i++) {
    percentileBelow += distribution[levels[i]];
  }
  // Add half of the percentage at user's level (assuming uniform distribution within level)
  percentileBelow += distribution[skill.level] / 2;
  // Calculate average company level (weighted average)
  const levelValues = {
    beginner: 0,
    intermediate: 25,
    advanced: 50,
    expert: 75,
    master: 100
  };
  let averageLevel = 0;
  Object.entries(distribution).forEach(([level, percentage]) => {
    averageLevel += levelValues[level as SkillLevel] * (percentage / 100);
  });
  return {
    skillId,
    averageLevel,
    distribution,
    userPercentile: Math.round(percentileBelow),
    totalUsers: Math.floor(Math.random() * 200) + 50,
    departmentAverage: Math.min(100, Math.max(0, averageLevel + (Math.random() * 20 - 10)))
  };
};
// Group skills by category
const groupedSkills = skillsData.reduce((acc, skill) => {
  if (!acc[skill.category]) {
    acc[skill.category] = [];
  }
  acc[skill.category].push(skill);
  return acc;
}, {} as Record<string, Skill[]>);
const SkillGraph = () => {
  const [expandedCategories, setExpandedCategories] = useState<Record<string, boolean>>({
    Technical: true,
    Leadership: true,
    Communication: true,
    'Domain Knowledge': true
  });
  const [selectedSkill, setSelectedSkill] = useState<Skill | null>(null);
  const [comparisonModalOpen, setComparisonModalOpen] = useState(false);
  const [comparisonSkill, setComparisonSkill] = useState<Skill | null>(null);
  const [comparisonData, setComparisonData] = useState<CompanyComparisonData | null>(null);
  const [assessmentModalOpen, setAssessmentModalOpen] = useState(false);
  const [assessmentSkill, setAssessmentSkill] = useState<Skill | null>(null);
  const toggleCategory = (category: string) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };
  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Not assessed yet';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };
  const openComparisonModal = (skill: Skill, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent triggering the parent onClick
    setComparisonSkill(skill);
    setComparisonData(generateCompanyComparisonData(skill.id));
    setComparisonModalOpen(true);
  };
  const closeComparisonModal = () => {
    setComparisonModalOpen(false);
    setComparisonSkill(null);
    setComparisonData(null);
  };
  const openAssessmentModal = (skill: Skill, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent triggering the parent onClick
    setAssessmentSkill(skill);
    setAssessmentModalOpen(true);
  };
  const closeAssessmentModal = () => {
    setAssessmentModalOpen(false);
    setAssessmentSkill(null);
  };
  // Component for a single skill heatmap
  const SkillHeatmap = ({
    skill
  }: {
    skill: Skill;
  }) => {
    const position = levelToValue[skill.level];
    return <div className={`relative mb-4 p-4 border rounded-lg ${selectedSkill?.id === skill.id ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'}`} onClick={() => setSelectedSkill(skill)} data-id="element-960">
        <div className="flex justify-between items-center mb-2" data-id="element-961">
          <h3 className="font-medium text-gray-900" data-id="element-962">{skill.name}</h3>
          <div className="flex items-center space-x-2" data-id="element-963">
            {skill.growth && <div className="flex items-center text-xs text-green-600" data-id="element-964">
                <Zap className="w-3 h-3 mr-1" data-id="element-965" />+{skill.growth}%
              </div>}
            <button onClick={e => openAssessmentModal(skill, e)} className="text-xs flex items-center text-blue-600 hover:text-blue-800" title="View assessment history" data-id="element-966">
              <FileText className="w-3 h-3 mr-1" data-id="element-967" />
              <span className="hidden sm:inline" data-id="element-968">Assess</span>
            </button>
            <button onClick={e => openComparisonModal(skill, e)} className="text-xs flex items-center text-blue-600 hover:text-blue-800" title="Compare with company" data-id="element-969">
              <BarChart className="w-3 h-3 mr-1" data-id="element-970" />
              <span className="hidden sm:inline" data-id="element-971">Compare</span>
            </button>
          </div>
        </div>
        <div className="relative h-8 bg-gradient-to-r from-gray-200 via-blue-300 to-blue-600 rounded-md overflow-hidden" data-id="element-972">
          {/* Skill level markers */}
          <div className="absolute inset-0 flex justify-between px-2 text-xs text-white font-medium" data-id="element-973">
            <span className="self-center" data-id="element-974">Beginner</span>
            <span className="self-center" data-id="element-975">Master</span>
          </div>
          {/* User position marker */}
          <div className="absolute top-0 bottom-0 w-0.5 bg-red-500 z-10" style={{
          left: `${position}%`
        }} data-id="element-976">
            <div className="absolute -top-1 -left-2 w-4 h-4 bg-white border-2 border-red-500 rounded-full" data-id="element-977"></div>
          </div>
        </div>
        <div className="mt-2 flex justify-between text-xs text-gray-500" data-id="element-978">
          <span data-id="element-979">Last assessed: {formatDate(skill.lastAssessed)}</span>
          <span className={levelToColor[skill.level] + ' px-2 py-0.5 rounded-full text-white'} data-id="element-980">
            {skill.level.charAt(0).toUpperCase() + skill.level.slice(1)}
          </span>
        </div>
      </div>;
  };
  // Component for company comparison modal
  const ComparisonModal = () => {
    if (!comparisonSkill || !comparisonData) return null;
    const userLevel = levelToValue[comparisonSkill.level];
    const companyLevel = comparisonData.averageLevel;
    const userPercentile = comparisonData.userPercentile;
    return <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" data-id="element-981">
        <div className="bg-white rounded-lg shadow-xl w-11/12 max-w-3xl max-h-[90vh] flex flex-col" data-id="element-982">
          <div className="p-4 border-b flex justify-between items-center" data-id="element-983">
            <h3 className="text-lg font-medium text-gray-900" data-id="element-984">
              {comparisonSkill.name} - Company Comparison
            </h3>
            <button onClick={closeComparisonModal} className="text-gray-400 hover:text-gray-600" data-id="element-985">
              <X className="w-5 h-5" data-id="element-986" />
            </button>
          </div>
          <div className="flex-1 p-6 overflow-y-auto" data-id="element-987">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-988">
              {/* Left column - User stats */}
              <div className="space-y-6" data-id="element-989">
                <div className="bg-blue-50 p-4 rounded-lg border border-blue-100" data-id="element-990">
                  <div className="flex items-center mb-2" data-id="element-991">
                    <UserIcon className="w-5 h-5 text-blue-600 mr-2" data-id="element-992" />
                    <h4 className="font-medium" data-id="element-993">Your Skill Level</h4>
                  </div>
                  <div className="flex items-center mb-4" data-id="element-994">
                    <span className={`${levelToColor[comparisonSkill.level]} px-3 py-1 rounded-md text-white text-sm font-medium`} data-id="element-995">
                      {comparisonSkill.level.charAt(0).toUpperCase() + comparisonSkill.level.slice(1)}
                    </span>
                    {comparisonSkill.growth && <span className="ml-2 text-xs text-green-600 flex items-center" data-id="element-996">
                        <TrendingUp className="w-3 h-3 mr-1" data-id="element-997" />+
                        {comparisonSkill.growth}% growth
                      </span>}
                  </div>
                  <div className="text-sm text-gray-600" data-id="element-998">
                    <p data-id="element-999">
                      You are in the{' '}
                      <span className="font-medium text-blue-700" data-id="element-1000">
                        {userPercentile}th percentile
                      </span>{' '}
                      of all employees for this skill.
                    </p>
                    <p className="mt-1" data-id="element-1001">
                      Last assessed: {formatDate(comparisonSkill.lastAssessed)}
                    </p>
                  </div>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg border border-gray-200" data-id="element-1002">
                  <div className="flex items-center mb-3" data-id="element-1003">
                    <Info className="w-5 h-5 text-gray-500 mr-2" data-id="element-1004" />
                    <h4 className="font-medium" data-id="element-1005">Key Insights</h4>
                  </div>
                  <ul className="space-y-2 text-sm text-gray-600" data-id="element-1006">
                    <li className="flex items-start" data-id="element-1007">
                      <span className="text-blue-500 mr-2" data-id="element-1008">•</span>
                      {userLevel > companyLevel ? <span data-id="element-1009">
                          You are <span className="font-medium" data-id="element-1010">above</span> the
                          company average in this skill.
                        </span> : userLevel < companyLevel ? <span data-id="element-1011">
                          You are <span className="font-medium" data-id="element-1012">below</span> the
                          company average in this skill.
                        </span> : <span data-id="element-1013">
                          You are <span className="font-medium" data-id="element-1014">at</span> the
                          company average for this skill.
                        </span>}
                    </li>
                    <li className="flex items-start" data-id="element-1015">
                      <span className="text-blue-500 mr-2" data-id="element-1016">•</span>
                      <span data-id="element-1017">
                        {userPercentile > 75 ? <span data-id="element-1018">
                            You are among the{' '}
                            <span className="font-medium" data-id="element-1019">top performers</span>{' '}
                            in this skill.
                          </span> : userPercentile > 50 ? <span data-id="element-1020">
                            You are{' '}
                            <span className="font-medium" data-id="element-1021">above average</span>{' '}
                            compared to your peers.
                          </span> : userPercentile > 25 ? <span data-id="element-1022">
                            You have{' '}
                            <span className="font-medium" data-id="element-1023">room to grow</span> in
                            this skill area.
                          </span> : <span data-id="element-1024">
                            This is an{' '}
                            <span className="font-medium" data-id="element-1025">
                              opportunity area
                            </span>{' '}
                            for your development.
                          </span>}
                      </span>
                    </li>
                    <li className="flex items-start" data-id="element-1026">
                      <span className="text-blue-500 mr-2" data-id="element-1027">•</span>
                      <span data-id="element-1028">
                        {comparisonData.totalUsers} employees have been assessed
                        for this skill.
                      </span>
                    </li>
                  </ul>
                </div>
              </div>
              {/* Right column - Company distribution */}
              <div className="space-y-6" data-id="element-1029">
                <div className="bg-white p-4 rounded-lg border" data-id="element-1030">
                  <div className="flex items-center mb-4" data-id="element-1031">
                    <BarChart2 className="w-5 h-5 text-gray-700 mr-2" data-id="element-1032" />
                    <h4 className="font-medium" data-id="element-1033">Company Distribution</h4>
                  </div>
                  <div className="space-y-3" data-id="element-1034">
                    {/* Distribution bars */}
                    {(['master', 'expert', 'advanced', 'intermediate', 'beginner'] as SkillLevel[]).map(level => {
                    const percentage = comparisonData.distribution[level];
                    const isUserLevel = comparisonSkill.level === level;
                    return <div key={level} className="relative" data-id="element-1035">
                          <div className="flex justify-between text-xs text-gray-500 mb-1" data-id="element-1036">
                            <span className={isUserLevel ? 'font-medium text-blue-600' : ''} data-id="element-1037">
                              {level.charAt(0).toUpperCase() + level.slice(1)}
                              {isUserLevel && ' (You)'}
                            </span>
                            <span data-id="element-1038">{percentage}%</span>
                          </div>
                          <div className="h-5 bg-gray-200 rounded-sm overflow-hidden" data-id="element-1039">
                            <div className={`h-full ${isUserLevel ? 'bg-blue-500' : levelToColor[level]}`} style={{
                          width: `${percentage}%`
                        }} data-id="element-1040"></div>
                          </div>
                        </div>;
                  })}
                  </div>
                  <div className="mt-4 pt-4 border-t border-gray-100" data-id="element-1041">
                    <div className="flex justify-between text-sm" data-id="element-1042">
                      <div data-id="element-1043">
                        <span className="text-gray-500" data-id="element-1044">Company Average:</span>
                        <span className="ml-2 font-medium" data-id="element-1045">
                          {valueToLevel(comparisonData.averageLevel)}
                        </span>
                      </div>
                      {comparisonData.departmentAverage && <div data-id="element-1046">
                          <span className="text-gray-500" data-id="element-1047">
                            Department Average:
                          </span>
                          <span className="ml-2 font-medium" data-id="element-1048">
                            {valueToLevel(comparisonData.departmentAverage)}
                          </span>
                        </div>}
                    </div>
                  </div>
                </div>
                <div className="bg-blue-50 p-4 rounded-lg border border-blue-100" data-id="element-1049">
                  <div className="flex items-center mb-3" data-id="element-1050">
                    <Award className="w-5 h-5 text-blue-600 mr-2" data-id="element-1051" />
                    <h4 className="font-medium" data-id="element-1052">Recommendations</h4>
                  </div>
                  <div className="text-sm text-gray-600" data-id="element-1053">
                    {userPercentile > 75 ? <p data-id="element-1054">
                        Consider mentoring others in this skill area. Your
                        expertise could help elevate team performance.
                      </p> : userPercentile > 50 ? <p data-id="element-1055">
                        To reach expert level, focus on advanced applications
                        and deepen your knowledge in specialized areas.
                      </p> : userPercentile > 25 ? <p data-id="element-1056">
                        Build on your foundation by taking on more challenging
                        projects and seeking feedback from experts.
                      </p> : <p data-id="element-1057">
                        This is a growth opportunity. Consider training courses
                        and guided practice to build your capabilities.
                      </p>}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="border-t p-4 flex justify-end" data-id="element-1058">
            <button onClick={closeComparisonModal} className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md mr-2" data-id="element-1059">
              Close
            </button>
            <button className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md" data-id="element-1060">
              Update Skill
            </button>
          </div>
        </div>
      </div>;
  };
  // Component for assessment history modal
  const AssessmentHistoryModal = () => {
    if (!assessmentSkill) return null;
    const history = assessmentSkill.assessmentHistory || [];
    // Calculate the weighted impact for each assessment entry
    const calculateWeightedScore = (entry: AssessmentEntry) => {
      return entry.impact * entry.weight;
    };
    // Calculate total weighted score
    const totalWeightedScore = history.reduce((sum, entry) => sum + calculateWeightedScore(entry), 0);
    // Group assessments by factor
    const groupedByFactor = history.reduce((acc, entry) => {
      if (!acc[entry.factor]) {
        acc[entry.factor] = [];
      }
      acc[entry.factor].push(entry);
      return acc;
    }, {} as Record<AssessmentFactor, AssessmentEntry[]>);
    // Calculate factor contribution to overall score
    const factorContributions = Object.entries(groupedByFactor).map(([factor, entries]) => {
      const factorTotal = entries.reduce((sum, entry) => sum + calculateWeightedScore(entry), 0);
      return {
        factor: factor as AssessmentFactor,
        contribution: factorTotal / totalWeightedScore * 100,
        entries
      };
    }).sort((a, b) => b.contribution - a.contribution);
    return <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" data-id="element-1061">
        <div className="bg-white rounded-lg shadow-xl w-11/12 max-w-3xl max-h-[90vh] flex flex-col" data-id="element-1062">
          <div className="p-4 border-b flex justify-between items-center" data-id="element-1063">
            <h3 className="text-lg font-medium text-gray-900" data-id="element-1064">
              {assessmentSkill.name} - Assessment History
            </h3>
            <button onClick={closeAssessmentModal} className="text-gray-400 hover:text-gray-600" data-id="element-1065">
              <X className="w-5 h-5" data-id="element-1066" />
            </button>
          </div>
          <div className="flex-1 p-6 overflow-y-auto" data-id="element-1067">
            {history.length === 0 ? <div className="text-center py-8 text-gray-500" data-id="element-1068">
                <FileText className="w-12 h-12 mx-auto mb-3 text-gray-300" data-id="element-1069" />
                <p data-id="element-1070">No detailed assessment history available for this skill.</p>
                <p className="text-sm mt-2" data-id="element-1071">
                  Complete an assessment to see how your skill level is
                  calculated.
                </p>
              </div> : <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1072">
                {/* Left column - Overall score breakdown */}
                <div className="space-y-6" data-id="element-1073">
                  <div className="bg-blue-50 p-4 rounded-lg border border-blue-100" data-id="element-1074">
                    <div className="flex items-center mb-3" data-id="element-1075">
                      <PieChart className="w-5 h-5 text-blue-600 mr-2" data-id="element-1076" />
                      <h4 className="font-medium" data-id="element-1077">Score Composition</h4>
                    </div>
                    <div className="text-sm text-gray-600 mb-4" data-id="element-1078">
                      Your {assessmentSkill.name} skill level is calculated
                      based on the following factors:
                    </div>
                    {/* Factor contribution bars */}
                    <div className="space-y-3" data-id="element-1079">
                      {factorContributions.map(({
                    factor,
                    contribution
                  }) => <div key={factor} className="relative" data-id="element-1080">
                          <div className="flex justify-between text-xs text-gray-700 mb-1" data-id="element-1081">
                            <span className="flex items-center" data-id="element-1082">
                              <span className="w-3 h-3 rounded-sm mr-1.5" style={{
                          backgroundColor: factorToColor[factor].replace('bg-', '')
                        }} data-id="element-1083"></span>
                              {factorToName[factor]}
                            </span>
                            <span data-id="element-1084">{contribution.toFixed(1)}%</span>
                          </div>
                          <div className="h-4 bg-gray-100 rounded-sm overflow-hidden" data-id="element-1085">
                            <div className={`h-full ${factorToColor[factor]}`} style={{
                        width: `${contribution}%`
                      }} data-id="element-1086"></div>
                          </div>
                        </div>)}
                    </div>
                    <div className="mt-4 pt-4 border-t border-blue-100" data-id="element-1087">
                      <div className="flex justify-between items-center" data-id="element-1088">
                        <span className="text-sm text-gray-600" data-id="element-1089">
                          Current Level:
                        </span>
                        <span className={`${levelToColor[assessmentSkill.level]} px-2 py-0.5 rounded-md text-white text-sm font-medium`} data-id="element-1090">
                          {assessmentSkill.level.charAt(0).toUpperCase() + assessmentSkill.level.slice(1)}
                        </span>
                      </div>
                      <div className="flex justify-between items-center mt-2" data-id="element-1091">
                        <span className="text-sm text-gray-600" data-id="element-1092">
                          Last Assessment:
                        </span>
                        <span className="text-sm font-medium" data-id="element-1093">
                          {formatDate(assessmentSkill.lastAssessed)}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg border border-gray-200" data-id="element-1094">
                    <div className="flex items-center mb-3" data-id="element-1095">
                      <Info className="w-5 h-5 text-gray-600 mr-2" data-id="element-1096" />
                      <h4 className="font-medium" data-id="element-1097">Assessment Methodology</h4>
                    </div>
                    <div className="text-sm text-gray-600 space-y-2" data-id="element-1098">
                      <p data-id="element-1099">
                        Skill levels are calculated using a weighted scoring
                        system that considers multiple factors:
                      </p>
                      <ul className="space-y-1 mt-2" data-id="element-1100">
                        <li className="flex items-start" data-id="element-1101">
                          <span className="text-blue-500 mr-2" data-id="element-1102">•</span>
                          <span data-id="element-1103">
                            <strong data-id="element-1104">Project Experience:</strong> Practical
                            application in real projects
                          </span>
                        </li>
                        <li className="flex items-start" data-id="element-1105">
                          <span className="text-blue-500 mr-2" data-id="element-1106">•</span>
                          <span data-id="element-1107">
                            <strong data-id="element-1108">Training & Classes:</strong> Formal
                            education and certifications
                          </span>
                        </li>
                        <li className="flex items-start" data-id="element-1109">
                          <span className="text-blue-500 mr-2" data-id="element-1110">•</span>
                          <span data-id="element-1111">
                            <strong data-id="element-1112">Mentoring Activities:</strong> Knowledge
                            sharing and teaching others
                          </span>
                        </li>
                        <li className="flex items-start" data-id="element-1113">
                          <span className="text-blue-500 mr-2" data-id="element-1114">•</span>
                          <span data-id="element-1115">
                            <strong data-id="element-1116">Business Outcomes:</strong> Measurable
                            impact on business goals
                          </span>
                        </li>
                        <li className="flex items-start" data-id="element-1117">
                          <span className="text-blue-500 mr-2" data-id="element-1118">•</span>
                          <span data-id="element-1119">
                            <strong data-id="element-1120">Peer Review:</strong> Feedback from
                            colleagues and managers
                          </span>
                        </li>
                      </ul>
                      <p className="mt-2" data-id="element-1121">
                        Each factor has a different weight in the overall
                        assessment based on its relevance to demonstrating
                        mastery of the skill.
                      </p>
                    </div>
                  </div>
                </div>
                {/* Right column - Assessment entries */}
                <div className="space-y-4" data-id="element-1122">
                  <h4 className="font-medium text-gray-900 mb-2" data-id="element-1123">
                    Assessment History
                  </h4>
                  {history.map(entry => <div key={entry.id} className="border rounded-lg overflow-hidden" data-id="element-1124">
                      <div className={`${factorToColor[entry.factor].replace('bg-', 'bg-').replace('500', '100')} px-4 py-2 border-b flex justify-between items-center`} data-id="element-1125">
                        <div className="flex items-center" data-id="element-1126">
                          <div className={`w-6 h-6 rounded-full flex items-center justify-center ${factorToColor[entry.factor]} text-white mr-2`} data-id="element-1127">
                            {factorToIcon(entry.factor)}
                          </div>
                          <span className="font-medium text-gray-800" data-id="element-1128">
                            {factorToName[entry.factor]}
                          </span>
                        </div>
                        <div className="flex items-center space-x-3" data-id="element-1129">
                          <span className="text-xs text-gray-500 flex items-center" data-id="element-1130">
                            <Calendar className="w-3 h-3 mr-1" data-id="element-1131" />
                            {formatDate(entry.date)}
                          </span>
                          <span className={`${factorToColor[entry.factor]} px-2 py-0.5 rounded-full text-white text-xs font-medium`} data-id="element-1132">
                            Impact: {entry.impact}/100
                          </span>
                        </div>
                      </div>
                      <div className="p-3 bg-white" data-id="element-1133">
                        <p className="text-sm text-gray-700" data-id="element-1134">
                          {entry.description}
                        </p>
                        <div className="mt-2 flex justify-between items-center" data-id="element-1135">
                          <span className="text-xs text-gray-500" data-id="element-1136">
                            Weight: {(entry.weight * 100).toFixed(0)}% of
                            overall score
                          </span>
                          <span className="text-xs font-medium text-blue-600" data-id="element-1137">
                            Contribution:{' '}
                            {(calculateWeightedScore(entry) / totalWeightedScore * 100).toFixed(1)}
                            %
                          </span>
                        </div>
                      </div>
                    </div>)}
                </div>
              </div>}
          </div>
          <div className="border-t p-4 flex justify-end" data-id="element-1138">
            <button onClick={closeAssessmentModal} className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md mr-2" data-id="element-1139">
              Close
            </button>
            <button className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md" data-id="element-1140">
              Update Assessment
            </button>
          </div>
        </div>
      </div>;
  };
  return <div className="flex flex-col h-full border rounded-lg bg-white shadow-sm overflow-hidden" data-id="element-1141">
      <div className="px-4 py-3 border-b bg-gray-50" data-id="element-1142">
        <h2 className="text-lg font-medium text-gray-900" data-id="element-1143">
          Capacity Dashboard
        </h2>
        <p className="text-sm text-gray-500" data-id="element-1144">
          View your skill levels and capacity across different domains
        </p>
      </div>
      <div className="flex h-full overflow-hidden" data-id="element-1145">
        {/* Main content - skill heatmaps */}
        <div className="flex-1 p-4 overflow-y-auto" data-id="element-1146">
          <div className="flex flex-col w-full space-y-6" data-id="element-1147">
            {Object.entries(groupedSkills).map(([category, skills]) => <div key={category} className="border rounded-lg p-4 w-full" data-id="element-1148">
                <button className="flex items-center justify-between w-full text-left mb-3" onClick={() => toggleCategory(category)} data-id="element-1149">
                  <div className="flex items-center" data-id="element-1150">
                    {category === 'Technical' && <BookOpen className="w-5 h-5 mr-2 text-blue-500" data-id="element-1151" />}
                    {category === 'Leadership' && <Users className="w-5 h-5 mr-2 text-purple-500" data-id="element-1152" />}
                    {category === 'Communication' && <Briefcase className="w-5 h-5 mr-2 text-green-500" data-id="element-1153" />}
                    {category === 'Domain Knowledge' && <BarChart2 className="w-5 h-5 mr-2 text-orange-500" data-id="element-1154" />}
                    <h3 className="font-medium text-gray-900" data-id="element-1155">
                      {category} Skills
                    </h3>
                  </div>
                  {expandedCategories[category] ? <ChevronDown className="w-5 h-5 text-gray-400" data-id="element-1156" /> : <ChevronRight className="w-5 h-5 text-gray-400" data-id="element-1157" />}
                </button>
                {expandedCategories[category] && <div className="space-y-1" data-id="element-1158">
                    {skills.map(skill => <SkillHeatmap key={skill.id} skill={skill} data-id="element-1159" />)}
                  </div>}
              </div>)}
          </div>
        </div>
        {/* Sidebar - skill details */}
        <div className={`w-0 lg:w-80 border-l transition-all duration-300 ${selectedSkill ? 'lg:w-80' : 'lg:w-0'}`} data-id="element-1160">
          {selectedSkill && <div className="p-4 h-full overflow-y-auto" data-id="element-1161">
              <div className="flex justify-between items-start mb-4" data-id="element-1162">
                <h3 className="font-medium text-lg" data-id="element-1163">{selectedSkill.name}</h3>
                <button onClick={() => setSelectedSkill(null)} className="text-gray-400 hover:text-gray-600" data-id="element-1164">
                  &times;
                </button>
              </div>
              <div className="space-y-4" data-id="element-1165">
                <div data-id="element-1166">
                  <div className="text-sm text-gray-500 mb-1" data-id="element-1167">Category</div>
                  <div className="font-medium" data-id="element-1168">{selectedSkill.category}</div>
                </div>
                <div data-id="element-1169">
                  <div className="text-sm text-gray-500 mb-1" data-id="element-1170">
                    Current Level
                  </div>
                  <div className="flex items-center" data-id="element-1171">
                    <span className={`${levelToColor[selectedSkill.level]} px-2 py-1 rounded-md text-white text-sm`} data-id="element-1172">
                      {selectedSkill.level.charAt(0).toUpperCase() + selectedSkill.level.slice(1)}
                    </span>
                    {selectedSkill.growth && <span className="ml-2 text-xs text-green-600 flex items-center" data-id="element-1173">
                        <Zap className="w-3 h-3 mr-1" data-id="element-1174" />+{selectedSkill.growth}%
                        growth
                      </span>}
                  </div>
                </div>
                <div data-id="element-1175">
                  <div className="text-sm text-gray-500 mb-1" data-id="element-1176">Description</div>
                  <p className="text-sm" data-id="element-1177">{selectedSkill.description}</p>
                </div>
                <div data-id="element-1178">
                  <div className="text-sm text-gray-500 mb-1" data-id="element-1179">
                    Last Assessment
                  </div>
                  <div className="text-sm" data-id="element-1180">
                    {formatDate(selectedSkill.lastAssessed)}
                  </div>
                </div>
                <div className="pt-4 border-t" data-id="element-1181">
                  <div className="flex items-center text-sm text-gray-500 mb-2" data-id="element-1182">
                    <Info className="w-4 h-4 mr-1" data-id="element-1183" />
                    <span data-id="element-1184">Skill Development Path</span>
                  </div>
                  <div className="space-y-2 text-sm" data-id="element-1185">
                    <div className="flex items-center" data-id="element-1186">
                      <div className="w-4 h-4 rounded-full bg-gray-200 flex items-center justify-center text-xs mr-2" data-id="element-1187">
                        1
                      </div>
                      <span className={selectedSkill.level === 'beginner' ? 'font-medium' : ''} data-id="element-1188">
                        Beginner
                      </span>
                    </div>
                    <div className="flex items-center" data-id="element-1189">
                      <div className="w-4 h-4 rounded-full bg-gray-200 flex items-center justify-center text-xs mr-2" data-id="element-1190">
                        2
                      </div>
                      <span className={selectedSkill.level === 'intermediate' ? 'font-medium' : ''} data-id="element-1191">
                        Intermediate
                      </span>
                    </div>
                    <div className="flex items-center" data-id="element-1192">
                      <div className="w-4 h-4 rounded-full bg-gray-200 flex items-center justify-center text-xs mr-2" data-id="element-1193">
                        3
                      </div>
                      <span className={selectedSkill.level === 'advanced' ? 'font-medium' : ''} data-id="element-1194">
                        Advanced
                      </span>
                    </div>
                    <div className="flex items-center" data-id="element-1195">
                      <div className="w-4 h-4 rounded-full bg-gray-200 flex items-center justify-center text-xs mr-2" data-id="element-1196">
                        4
                      </div>
                      <span className={selectedSkill.level === 'expert' ? 'font-medium' : ''} data-id="element-1197">
                        Expert
                      </span>
                    </div>
                    <div className="flex items-center" data-id="element-1198">
                      <div className="w-4 h-4 rounded-full bg-gray-200 flex items-center justify-center text-xs mr-2" data-id="element-1199">
                        5
                      </div>
                      <span className={selectedSkill.level === 'master' ? 'font-medium' : ''} data-id="element-1200">
                        Master
                      </span>
                    </div>
                  </div>
                </div>
                <div className="pt-4 flex space-x-2" data-id="element-1201">
                  <button className="flex-1 py-2 bg-blue-100 text-blue-700 rounded-md text-sm hover:bg-blue-200 transition-colors" onClick={e => openAssessmentModal(selectedSkill, e as React.MouseEvent)} data-id="element-1202">
                    <FileText className="w-4 h-4 inline mr-1" data-id="element-1203" />
                    Assess
                  </button>
                  <button className="py-2 px-3 bg-gray-100 text-gray-700 rounded-md text-sm hover:bg-gray-200 transition-colors flex items-center" onClick={e => openComparisonModal(selectedSkill, e as React.MouseEvent)} data-id="element-1204">
                    <BarChart className="w-4 h-4 mr-1" data-id="element-1205" />
                    Compare
                  </button>
                </div>
              </div>
            </div>}
        </div>
      </div>
      {/* Company comparison modal */}
      {comparisonModalOpen && <ComparisonModal data-id="element-1206" />}
      {/* Assessment history modal */}
      {assessmentModalOpen && <AssessmentHistoryModal data-id="element-1207" />}
    </div>;
};
export default SkillGraph;